import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsiveLine } from "@nivo/line";
import { useAuth } from "../../contexts/AuthContext";
import { getDayPost, generateDateID } from "../../services/dayPostService";
import { DayPost, HealthLog } from "../../types/post";
import { useSubscription } from "../../providers/SubscriptionProvider";
import { useThemeStore } from "../../stores/themeStore";

import {
  ChevronLeftIcon,
  XMarkIcon,
  LockClosedIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CalendarIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";
import SleepTimeline from "../../components/common/SleepTimeline";
import { calcSleepFromHealthLogs, calcSleepFromTimeline } from "../../utils";
import Header from "../../components/layout/Header";
import { SparklesIcon } from "@heroicons/react/24/solid";
import RecoveryCard from "../../components/dashboard/cards/HealthStatsCard";
import { userService } from "../../services/userService";
import { derivedDataAttribution } from "../../utils/providerAttribution";
import ComingSoon from "../../components/ComingSoon";

interface SleepData {
  date: string;
  dateLabel: string;
  score: number;
  duration: number;
  hrv?: number;
  rhr?: number;
  respRate?: number;
  hasData: boolean;
  [key: string]: any; // Index signature for Nivo compatibility
}

const SleepInsights: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [sleepData, setSleepData] = useState<SleepData[]>([]);
  const [sleepDurationData, setSleepDurationData] = useState<SleepData[]>([]);
  const [monthlySleepData, setMonthlySleepData] = useState<SleepData[]>([]);
  const [monthlyDurationData, setMonthlyDurationData] = useState<SleepData[]>(
    [],
  );
  const [healthMetricsData, setHealthMetricsData] = useState<SleepData[]>([]);
  const [monthlyHealthMetricsData, setMonthlyHealthMetricsData] = useState<
    SleepData[]
  >([]);
  const [quarterlySleepData, setQuarterlySleepData] = useState<SleepData[]>([]);
  const [quarterlyDurationData, setQuarterlyDurationData] = useState<
    SleepData[]
  >([]);
  const [quarterlyHealthMetricsData, setQuarterlyHealthMetricsData] = useState<
    SleepData[]
  >([]);
  const [currentDayHealth, setCurrentDayHealth] = useState<HealthLog | null>(
    null,
  );
  // For "Combined or Derived Data" attribution on trend charts (issue #169)
  // — not tied to any specific chart's data points, just whether Garmin is
  // a plausible contributing source for this user at all.
  const [hasActiveGarminDevice, setHasActiveGarminDevice] = useState(false);
  const [currentDayHLR, setCurrentDayHLR] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [healthMetricsComparison, setHealthMetricsComparison] = useState({
    rhrPercentDiff: 0,
    hrvPercentDiff: 0,
    respPercentDiff: 0,
  });
  const [selectedTab, setSelectedTab] = useState<
    "Day" | "Week" | "Month" | "Quarter" | "Custom"
  >("Day");
  const [showInfoDialog, setShowInfoDialog] = useState(false);
  const [isSleepTimelineExpanded, setIsSleepTimelineExpanded] = useState(false);
  const [showDateRangeDialog, setShowDateRangeDialog] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const {
    subscription: userSubscription,
    isPremium,
    isPlusOrHigher,
    refreshSubscription,
  } = useSubscription();

  const { resolvedTheme } = useThemeStore();
  const isDark = resolvedTheme === "peakx-dark";
  const nivoTheme = {
    axis: {
      ticks: {
        text: {
          fill: isDark ? "#e5e5e5" : "#374151",
        },
      },
    },
    grid: {
      line: {
        stroke: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)",
        strokeWidth: 1,
      },
    },
  };

  // Custom date range state
  const [showCustomDateModal, setShowCustomDateModal] = useState(false);
  const [customStartDate, setCustomStartDate] = useState<string>("");
  const [customEndDate, setCustomEndDate] = useState<string>("");
  const [customRangeSleepData, setCustomRangeSleepData] = useState<SleepData[]>(
    [],
  );
  const [customRangeDurationData, setCustomRangeDurationData] = useState<
    SleepData[]
  >([]);
  const [customRangeHealthMetricsData, setCustomRangeHealthMetricsData] =
    useState<SleepData[]>([]);
  const [customRangeLoading, setCustomRangeLoading] = useState(false);

  // Plus: Month (28 days). Premium: Quarter + Custom.
  const hasMonthlyAccess = isPlusOrHigher;
  const hasQuarterlyAccess = isPremium;
  const hasCustomDateAccess = isPremium;
  const hasHLRAccess = isPlusOrHigher;

  // Get date from URL parameter, default to today
  const dateParam = searchParams.get("date");
  const targetDate = useMemo(() => {
    if (dateParam && /^\d{8}$/.test(dateParam)) {
      const year = parseInt(dateParam.substring(0, 4));
      const month = parseInt(dateParam.substring(4, 6)) - 1; // Month is 0-indexed
      const day = parseInt(dateParam.substring(6, 8));
      return new Date(year, month, day);
    }
    return new Date();
  }, [dateParam]);

  // Generate past 7 days including the target date
  const generatePastWeekDates = (endDate: Date): Date[] => {
    const dates: Date[] = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date(endDate);
      date.setDate(date.getDate() - i);
      dates.push(date);
    }
    return dates;
  };

  // Generate past 30 days
  const generatePast30Days = (endDate: Date): Date[] => {
    const dates: Date[] = [];
    for (let i = 29; i >= 0; i--) {
      // Get 30 days of data
      const date = new Date(endDate);
      date.setDate(date.getDate() - i);
      dates.push(date);
    }
    return dates;
  };

  // Generate past 12 weeks (returns the end date of each week)
  const generatePast12Weeks = (endDate: Date): Date[] => {
    const weeks: Date[] = [];
    for (let i = 0; i < 12; i++) {
      const weekEndDate = new Date(endDate);
      weekEndDate.setDate(weekEndDate.getDate() - i * 7);
      weeks.push(weekEndDate);
    }
    return weeks.reverse(); // Reverse to get chronological order
  };

  // Calculate health score from PeakScoreLog.hlr (0-100 presumed scale)
  const extractHLRScore = (dayPost: DayPost | null): number => {
    const hlr = dayPost?.peakScoreLog?.hlr;
    return typeof hlr === "number" && Number.isFinite(hlr) ? hlr : 0;
  };

  // Calculate sleep duration in hours from HealthLog
  const calculateSleepDuration = (healthLogs: HealthLog[]): number => {
    const durationInSeconds = calcSleepFromHealthLogs(healthLogs);
    return durationInSeconds / 3600; // Convert seconds to hours
  };

  // Format date for display
  const formatDateLabel = (date: Date): string => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return "Today";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      });
    }
  };

  // Whether to show Garmin's "derived data" attribution on trend charts —
  // independent of the sleep-data fetch below, cheap, and doesn't need to
  // block the chart render.
  useEffect(() => {
    if (!currentUser?.uid) return;
    userService
      .getUserData(currentUser.uid)
      .then((userData) => {
        const devices = (userData?.deviceIDs as { resource: string; expired?: boolean }[] | undefined) ?? [];
        setHasActiveGarminDevice(
          devices.some((d) => d.resource === "GARMIN" && !d.expired),
        );
      })
      .catch(() => setHasActiveGarminDevice(false));
  }, [currentUser?.uid]);

  // Fetch all sleep data when component mounts or date changes
  useEffect(() => {
    const fetchSleepData = async () => {
      if (!currentUser?.uid) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch current day's detailed health data
        const dateID = generateDateID(targetDate);
        const dayPost = await getDayPost(currentUser.uid, dateID);
        if (dayPost && dayPost.healthLogs.length > 0) {
          setCurrentDayHealth(
            getLongestHealthLog(dayPost.healthLogs)
          );
        } else {
          setCurrentDayHealth(null);
        }

        // Set current day HLR score from peakScoreLog
        setCurrentDayHLR(extractHLRScore(dayPost));

        // Fetch week data
        const weekDates = generatePastWeekDates(targetDate);
        const sleepPromises = weekDates.map(async (date) => {
          const dateID = generateDateID(date);
          try {
            const dayPost = await getDayPost(currentUser.uid, dateID);
            const score = extractHLRScore(dayPost);
            const duration = dayPost
              ? calculateSleepDuration(dayPost.healthLogs)
              : 0;

            // Extract health metrics from the latest health log
            // const latestHealthLog =
            //   dayPost && dayPost.healthLogs.length > 0
            //     ? dayPost.healthLogs[dayPost.healthLogs.length - 1]
            //     : null;
            const latestHealthLog = dayPost ? getLongestHealthLog(dayPost.healthLogs) : null;

            // Two datasets will consume hasData independently
            return {
              date: dateID,
              dateLabel: formatDateLabel(date),
              score,
              duration,
              hrv: latestHealthLog?.hrv || 0,
              rhr: latestHealthLog?.rhr || 0,
              respRate: latestHealthLog?.respRate || 0,
              hasData:
                typeof dayPost?.peakScoreLog?.hlr === "number" &&
                Number.isFinite(dayPost?.peakScoreLog?.hlr as number),
              durationHasData:
                dayPost !== null &&
                dayPost.healthLogs.length > 0 &&
                dayPost.healthLogs[dayPost.healthLogs.length - 1]
                  .sleepDuration !== undefined,
              healthMetricsHasData:
                latestHealthLog !== null &&
                (latestHealthLog.hrv !== undefined ||
                  latestHealthLog.rhr !== undefined ||
                  latestHealthLog.respRate !== undefined),
            } as any;
          } catch (error) {
            console.error(`Error fetching data for ${dateID}:`, error);
            return {
              date: dateID,
              dateLabel: formatDateLabel(date),
              score: 0,
              duration: 0,
              hrv: 0,
              rhr: 0,
              respRate: 0,
              hasData: false,
              durationHasData: false,
              healthMetricsHasData: false,
            };
          }
        });

        const weekResults = await Promise.all(sleepPromises);
        // For score chart, use hasData based on HLR presence
        setSleepData(
          weekResults.map((d: any) => ({
            date: d.date,
            dateLabel: d.dateLabel,
            score: d.score,
            duration: d.duration,
            hasData: d.hasData,
          })),
        );
        // For duration chart, use hasData based on duration presence
        setSleepDurationData(
          weekResults.map((d: any) => ({
            date: d.date,
            dateLabel: d.dateLabel,
            score: d.score,
            duration: d.duration,
            hasData: d.durationHasData,
          })),
        );
        // For health metrics chart, use hasData based on health metrics presence
        setHealthMetricsData(
          weekResults.map((d: any) => ({
            date: d.date,
            dateLabel: d.dateLabel,
            score: d.score,
            duration: d.duration,
            hrv: d.hrv,
            rhr: d.rhr,
            respRate: d.respRate,
            hasData: d.healthMetricsHasData,
          })),
        );

        // Fetch month data only when Plus+ — free accounts must not receive 28-day charts.
        if (!hasMonthlyAccess) {
          setMonthlySleepData([]);
          setMonthlyDurationData([]);
          setMonthlyHealthMetricsData([]);
        } else {
        const monthDates = generatePast30Days(targetDate);
        const monthPromises = monthDates.map(async (date) => {
          const dateID = generateDateID(date);
          try {
            const dayPost = await getDayPost(currentUser.uid, dateID);
            const score = extractHLRScore(dayPost);
            const duration = dayPost
              ? calculateSleepDuration(dayPost.healthLogs)
              : 0;

            // Extract health metrics from the latest health log
            const latestHealthLog =
              dayPost && dayPost.healthLogs.length > 0
                ? dayPost.healthLogs[dayPost.healthLogs.length - 1]
                : null;

            return {
              date: dateID,
              dateLabel: date.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              }),
              score,
              duration,
              hrv: latestHealthLog?.hrv || 0,
              rhr: latestHealthLog?.rhr || 0,
              respRate: latestHealthLog?.respRate || 0,
              hasData:
                typeof dayPost?.peakScoreLog?.hlr === "number" &&
                Number.isFinite(dayPost?.peakScoreLog?.hlr as number),
              durationHasData:
                dayPost !== null &&
                dayPost.healthLogs.length > 0 &&
                dayPost.healthLogs[dayPost.healthLogs.length - 1]
                  .sleepDuration !== undefined,
              healthMetricsHasData:
                latestHealthLog !== null &&
                (latestHealthLog.hrv !== undefined ||
                  latestHealthLog.rhr !== undefined ||
                  latestHealthLog.respRate !== undefined),
            } as any;
          } catch (error) {
            console.error(`Error fetching data for ${dateID}:`, error);
            return {
              date: dateID,
              dateLabel: date.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              }),
              score: 0,
              duration: 0,
              hrv: 0,
              rhr: 0,
              respRate: 0,
              hasData: false,
              durationHasData: false,
              healthMetricsHasData: false,
            };
          }
        });

        const monthResults = await Promise.all(monthPromises);
        setMonthlySleepData(
          monthResults.map((d: any) => ({
            date: d.date,
            dateLabel: d.dateLabel,
            score: d.score,
            duration: d.duration,
            hasData: d.hasData,
          })),
        );
        setMonthlyDurationData(
          monthResults.map((d: any) => ({
            date: d.date,
            dateLabel: d.dateLabel,
            score: d.score,
            duration: d.duration,
            hasData: d.durationHasData,
          })),
        );
        setMonthlyHealthMetricsData(
          monthResults.map((d: any) => ({
            date: d.date,
            dateLabel: d.dateLabel,
            score: d.score,
            duration: d.duration,
            hrv: d.hrv,
            rhr: d.rhr,
            respRate: d.respRate,
            hasData: d.healthMetricsHasData,
          })),
        );
        }

        // Fetch quarterly data (12 weeks aggregated by week)
        if (hasQuarterlyAccess) {
          const quarterWeeks = generatePast12Weeks(targetDate);
          const quarterPromises = quarterWeeks.map(async (weekEndDate) => {
            // Get all 7 days for this week
            const weekDates: Date[] = [];
            for (let i = 6; i >= 0; i--) {
              const date = new Date(weekEndDate);
              date.setDate(date.getDate() - i);
              weekDates.push(date);
            }

            // Fetch data for all days in the week
            const weekDataPromises = weekDates.map(async (date) => {
              const dateID = generateDateID(date);
              try {
                const dayPost = await getDayPost(currentUser.uid, dateID);
                const score = extractHLRScore(dayPost);
                const duration = dayPost
                  ? calculateSleepDuration(dayPost.healthLogs)
                  : 0;

                const latestHealthLog =
                  dayPost && dayPost.healthLogs.length > 0
                    ? dayPost.healthLogs[dayPost.healthLogs.length - 1]
                    : null;

                return {
                  score,
                  duration,
                  hrv: latestHealthLog?.hrv || 0,
                  rhr: latestHealthLog?.rhr || 0,
                  respRate: latestHealthLog?.respRate || 0,
                  hasScore:
                    typeof dayPost?.peakScoreLog?.hlr === "number" &&
                    Number.isFinite(dayPost?.peakScoreLog?.hlr as number),
                  hasDuration:
                    dayPost !== null &&
                    dayPost.healthLogs.length > 0 &&
                    dayPost.healthLogs[dayPost.healthLogs.length - 1]
                      .sleepDuration !== undefined,
                  hasHealthMetrics:
                    latestHealthLog !== null &&
                    (latestHealthLog.hrv !== undefined ||
                      latestHealthLog.rhr !== undefined ||
                      latestHealthLog.respRate !== undefined),
                };
              } catch (error) {
                return {
                  score: 0,
                  duration: 0,
                  hrv: 0,
                  rhr: 0,
                  respRate: 0,
                  hasScore: false,
                  hasDuration: false,
                  hasHealthMetrics: false,
                };
              }
            });

            const weekData = await Promise.all(weekDataPromises);

            // Calculate weekly averages
            const validScores = weekData.filter((d) => d.hasScore);
            const validDurations = weekData.filter((d) => d.hasDuration);
            const validHealthMetrics = weekData.filter(
              (d) => d.hasHealthMetrics,
            );

            const avgScore =
              validScores.length > 0
                ? validScores.reduce((sum, d) => sum + d.score, 0) /
                  validScores.length
                : 0;
            const avgDuration =
              validDurations.length > 0
                ? validDurations.reduce((sum, d) => sum + d.duration, 0) /
                  validDurations.length
                : 0;
            const avgHrv =
              validHealthMetrics.length > 0
                ? validHealthMetrics.reduce((sum, d) => sum + d.hrv, 0) /
                  validHealthMetrics.length
                : 0;
            const avgRhr =
              validHealthMetrics.length > 0
                ? validHealthMetrics.reduce((sum, d) => sum + d.rhr, 0) /
                  validHealthMetrics.length
                : 0;
            const avgRespRate =
              validHealthMetrics.length > 0
                ? validHealthMetrics.reduce((sum, d) => sum + d.respRate, 0) /
                  validHealthMetrics.length
                : 0;

            // Format week label
            const weekStartDate = new Date(weekEndDate);
            weekStartDate.setDate(weekStartDate.getDate() - 6);
            const weekLabel = `${weekStartDate.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            })}`;

            return {
              date: generateDateID(weekEndDate),
              dateLabel: weekLabel,
              score: avgScore,
              duration: avgDuration,
              hrv: avgHrv,
              rhr: avgRhr,
              respRate: avgRespRate,
              hasData: validScores.length > 0,
              durationHasData: validDurations.length > 0,
              healthMetricsHasData: validHealthMetrics.length > 0,
            };
          });

          const quarterResults = await Promise.all(quarterPromises);

          setQuarterlySleepData(
            quarterResults.map((d: any) => ({
              date: d.date,
              dateLabel: d.dateLabel,
              score: d.score,
              duration: d.duration,
              hasData: d.hasData,
            })),
          );

          setQuarterlyDurationData(
            quarterResults.map((d: any) => ({
              date: d.date,
              dateLabel: d.dateLabel,
              score: d.score,
              duration: d.duration,
              hasData: d.durationHasData,
            })),
          );

          setQuarterlyHealthMetricsData(
            quarterResults.map((d: any) => ({
              date: d.date,
              dateLabel: d.dateLabel,
              score: d.score,
              duration: d.duration,
              hrv: d.hrv,
              rhr: d.rhr,
              respRate: d.respRate,
              hasData: d.healthMetricsHasData,
            })),
          );
        } else {
          setQuarterlySleepData([]);
          setQuarterlyDurationData([]);
          setQuarterlyHealthMetricsData([]);
        }

        // Calculate health metrics comparison (today vs 7-day average)
        const todayMetrics = currentDayHealth;
        if (todayMetrics) {
          // Get last 7 days of health metrics (excluding today)
          const last7DaysMetrics = weekResults
            .slice(0, -1)
            .filter((d: any) => d.healthMetricsHasData);

          if (last7DaysMetrics.length >= 3) {
            // Calculate 7-day averages
            const rhr7DayAvg =
              last7DaysMetrics.reduce(
                (sum: number, d: any) => sum + (d.rhr || 0),
                0,
              ) / last7DaysMetrics.length;
            const hrv7DayAvg =
              last7DaysMetrics.reduce(
                (sum: number, d: any) => sum + (d.hrv || 0),
                0,
              ) / last7DaysMetrics.length;
            const resp7DayAvg =
              last7DaysMetrics.reduce(
                (sum: number, d: any) => sum + (d.respRate || 0),
                0,
              ) / last7DaysMetrics.length;

            // Calculate percentage differences
            const calculatePercentDiff = (
              today: number,
              avg: number,
              lowerIsBetter: boolean = false,
            ): number => {
              if (today === 0 || avg === 0) return 0;
              const rawDiff = ((today - avg) / avg) * 100;
              return lowerIsBetter ? -rawDiff : rawDiff;
            };

            setHealthMetricsComparison({
              rhrPercentDiff: todayMetrics.rhr
                ? calculatePercentDiff(todayMetrics.rhr, rhr7DayAvg, true)
                : 0,
              hrvPercentDiff: todayMetrics.hrv
                ? calculatePercentDiff(todayMetrics.hrv, hrv7DayAvg, false)
                : 0,
              respPercentDiff: todayMetrics.respRate
                ? calculatePercentDiff(todayMetrics.respRate, resp7DayAvg, true)
                : 0,
            });
          }
        }
      } catch (err) {
        console.error("Error fetching sleep data:", err);
        setError("Failed to load sleep data");
      } finally {
        setLoading(false);
      }
    };

    fetchSleepData();
  }, [currentUser?.uid, targetDate, hasMonthlyAccess, hasQuarterlyAccess]);

  // Fetch custom date range data
  const fetchCustomRangeData = async (startDate: string, endDate: string) => {
    if (!currentUser?.uid || !startDate || !endDate) return;

    setCustomRangeLoading(true);
    setError(null);

    try {
      const start = new Date(startDate);
      const end = new Date(endDate);

      // Calculate the number of days in the range
      const daysDiff =
        Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) +
        1;
      const isOver30Days = daysDiff > 30;

      if (isOver30Days) {
        // For ranges over 30 days, group by weeks
        const weeks: Date[][] = [];
        let currentWeekStart = new Date(start);

        while (currentWeekStart <= end) {
          const weekEnd = new Date(currentWeekStart);
          weekEnd.setDate(weekEnd.getDate() + 6);

          // Don't go past the end date
          if (weekEnd > end) {
            weekEnd.setTime(end.getTime());
          }

          // Generate dates for this week
          const weekDates: Date[] = [];
          const current = new Date(currentWeekStart);
          while (current <= weekEnd) {
            weekDates.push(new Date(current));
            current.setDate(current.getDate() + 1);
          }

          weeks.push(weekDates);

          // Move to next week
          currentWeekStart.setDate(currentWeekStart.getDate() + 7);
        }

        // Fetch data for each week and calculate weekly averages
        const weekPromises = weeks.map(async (weekDates) => {
          const weekStart = weekDates[0];
          const weekEnd = weekDates[weekDates.length - 1];

          const weekLabel = `${weekStart.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          })} - ${weekEnd.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          })}`;

          // Fetch all days in the week
          const dayPromises = weekDates.map(async (date) => {
            const dateID = generateDateID(date);
            try {
              const dayPost = await getDayPost(currentUser.uid, dateID);
              const score = extractHLRScore(dayPost);
              const duration = dayPost
                ? calculateSleepDuration(dayPost.healthLogs)
                : 0;

              const latestHealthLog =
                dayPost && dayPost.healthLogs.length > 0
                  ? dayPost.healthLogs[dayPost.healthLogs.length - 1]
                  : null;

              return {
                score,
                duration,
                hrv: latestHealthLog?.hrv || 0,
                rhr: latestHealthLog?.rhr || 0,
                respRate: latestHealthLog?.respRate || 0,
                hasData: score > 0,
                durationHasData: duration > 0,
                healthMetricsHasData: !!(
                  latestHealthLog?.hrv ||
                  latestHealthLog?.rhr ||
                  latestHealthLog?.respRate
                ),
              };
            } catch (error) {
              return {
                score: 0,
                duration: 0,
                hrv: 0,
                rhr: 0,
                respRate: 0,
                hasData: false,
                durationHasData: false,
                healthMetricsHasData: false,
              };
            }
          });

          const weekData = await Promise.all(dayPromises);

          // Calculate weekly averages
          const validScores = weekData.filter((d) => d.hasData);
          const validDurations = weekData.filter((d) => d.durationHasData);
          const validHealthMetrics = weekData.filter(
            (d) => d.healthMetricsHasData,
          );

          const avgScore =
            validScores.length > 0
              ? validScores.reduce((sum, d) => sum + d.score, 0) /
                validScores.length
              : 0;

          const avgDuration =
            validDurations.length > 0
              ? validDurations.reduce((sum, d) => sum + d.duration, 0) /
                validDurations.length
              : 0;

          const avgHrv =
            validHealthMetrics.length > 0
              ? validHealthMetrics.reduce((sum, d) => sum + d.hrv, 0) /
                validHealthMetrics.length
              : 0;

          const avgRhr =
            validHealthMetrics.length > 0
              ? validHealthMetrics.reduce((sum, d) => sum + d.rhr, 0) /
                validHealthMetrics.length
              : 0;

          const avgRespRate =
            validHealthMetrics.length > 0
              ? validHealthMetrics.reduce((sum, d) => sum + d.respRate, 0) /
                validHealthMetrics.length
              : 0;

          return {
            date: generateDateID(weekStart),
            dateLabel: weekLabel,
            score: avgScore,
            duration: avgDuration,
            hrv: avgHrv,
            rhr: avgRhr,
            respRate: avgRespRate,
            hasData: validScores.length > 0,
            durationHasData: validDurations.length > 0,
            healthMetricsHasData: validHealthMetrics.length > 0,
          };
        });

        const weekResults = await Promise.all(weekPromises);

        setCustomRangeSleepData(
          weekResults.map((d) => ({
            date: d.date,
            dateLabel: d.dateLabel,
            score: d.score,
            duration: d.duration,
            hasData: d.hasData,
          })),
        );

        setCustomRangeDurationData(
          weekResults.map((d) => ({
            date: d.date,
            dateLabel: d.dateLabel,
            score: d.score,
            duration: d.duration,
            hasData: d.durationHasData,
          })),
        );

        setCustomRangeHealthMetricsData(
          weekResults.map((d) => ({
            date: d.date,
            dateLabel: d.dateLabel,
            score: d.score,
            duration: d.duration,
            hrv: d.hrv,
            rhr: d.rhr,
            respRate: d.respRate,
            hasData: d.healthMetricsHasData,
          })),
        );
      } else {
        // For ranges 30 days or less, show daily data
        const dates: Date[] = [];
        const current = new Date(start);
        while (current <= end) {
          dates.push(new Date(current));
          current.setDate(current.getDate() + 1);
        }

        const dayPromises = dates.map(async (date) => {
          const dateID = generateDateID(date);
          try {
            const dayPost = await getDayPost(currentUser.uid, dateID);
            const score = extractHLRScore(dayPost);
            const duration = dayPost
              ? calculateSleepDuration(dayPost.healthLogs)
              : 0;

            const latestHealthLog =
              dayPost && dayPost.healthLogs.length > 0
                ? dayPost.healthLogs[dayPost.healthLogs.length - 1]
                : null;

            return {
              date: dateID,
              dateLabel: date.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              }),
              score,
              duration,
              hrv: latestHealthLog?.hrv || 0,
              rhr: latestHealthLog?.rhr || 0,
              respRate: latestHealthLog?.respRate || 0,
              hasData: score > 0,
              durationHasData: duration > 0,
              healthMetricsHasData: !!(
                latestHealthLog?.hrv ||
                latestHealthLog?.rhr ||
                latestHealthLog?.respRate
              ),
            };
          } catch (error) {
            return {
              date: dateID,
              dateLabel: date.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              }),
              score: 0,
              duration: 0,
              hrv: 0,
              rhr: 0,
              respRate: 0,
              hasData: false,
              durationHasData: false,
              healthMetricsHasData: false,
            };
          }
        });

        const dayResults = await Promise.all(dayPromises);

        setCustomRangeSleepData(
          dayResults.map((d) => ({
            date: d.date,
            dateLabel: d.dateLabel,
            score: d.score,
            duration: d.duration,
            hasData: d.hasData,
          })),
        );

        setCustomRangeDurationData(
          dayResults.map((d) => ({
            date: d.date,
            dateLabel: d.dateLabel,
            score: d.score,
            duration: d.duration,
            hasData: d.durationHasData,
          })),
        );

        setCustomRangeHealthMetricsData(
          dayResults.map((d) => ({
            date: d.date,
            dateLabel: d.dateLabel,
            score: d.score,
            duration: d.duration,
            hrv: d.hrv,
            rhr: d.rhr,
            respRate: d.respRate,
            hasData: d.healthMetricsHasData,
          })),
        );
      }
    } catch (err) {
      console.error("Error fetching custom range data:", err);
      setError("Failed to load custom range data");
    } finally {
      setCustomRangeLoading(false);
    }
  };

  // Calculate average score based on selected tab
  const averageScore = useMemo(() => {
    if (selectedTab === "Day") {
      return currentDayHLR ?? 0;
    } else if (selectedTab === "Week") {
      const scoresWithData = sleepData.filter((d) => d.hasData);
      if (scoresWithData.length === 0) return 0;
      return (
        scoresWithData.reduce((sum, d) => sum + d.score, 0) /
        scoresWithData.length
      );
    } else if (selectedTab === "Month") {
      const scoresWithData = monthlySleepData.filter((d) => d.hasData);
      if (scoresWithData.length === 0) return 0;
      return (
        scoresWithData.reduce((sum, d) => sum + d.score, 0) /
        scoresWithData.length
      );
    } else if (selectedTab === "Quarter") {
      const scoresWithData = quarterlySleepData.filter((d) => d.hasData);
      if (scoresWithData.length === 0) return 0;
      return (
        scoresWithData.reduce((sum, d) => sum + d.score, 0) /
        scoresWithData.length
      );
    } else if (selectedTab === "Custom") {
      const scoresWithData = customRangeSleepData.filter((d) => d.hasData);
      if (scoresWithData.length === 0) return 0;
      return (
        scoresWithData.reduce((sum, d) => sum + d.score, 0) /
        scoresWithData.length
      );
    }
    return 0;
  }, [
    selectedTab,
    currentDayHLR,
    sleepData,
    monthlySleepData,
    quarterlySleepData,
    customRangeSleepData,
  ]);

  // Calculate linear regression trendline
  const calculateTrendline = (data: { x: string; y: number }[]) => {
    if (data.length < 2) return { trendlineData: [], percentChange: 0 };

    const n = data.length;
    const xValues = data.map((_, index) => index); // Use index as x for calculation
    const yValues = data.map((d) => d.y);

    // Calculate means
    const xMean = xValues.reduce((sum, x) => sum + x, 0) / n;
    const yMean = yValues.reduce((sum, y) => sum + y, 0) / n;

    // Calculate slope (m) and intercept (b) for y = mx + b
    let numerator = 0;
    let denominator = 0;

    for (let i = 0; i < n; i++) {
      numerator += (xValues[i] - xMean) * (yValues[i] - yMean);
      denominator += (xValues[i] - xMean) * (xValues[i] - xMean);
    }

    if (denominator === 0) return { trendlineData: [], percentChange: 0 };

    const slope = numerator / denominator;
    const intercept = yMean - slope * xMean;

    // Generate trendline points
    const trendlineData = data.map((point, index) => ({
      x: point.x,
      y: slope * index + intercept,
    }));

    // Calculate percentage change from first to last point of trendline
    const firstValue = intercept;
    const lastValue = slope * (n - 1) + intercept;
    const percentChange =
      firstValue !== 0 ? ((lastValue - firstValue) / firstValue) * 100 : 0;

    return { trendlineData, percentChange };
  };

  // Transform data for line charts
  const transformSleepDurationDataForLineChart = useMemo(() => {
    if (selectedTab === "Day")
      return { chartData: [], trendlinePercentChange: 0 };

    const data =
      selectedTab === "Week"
        ? sleepDurationData
        : selectedTab === "Quarter"
          ? quarterlyDurationData
          : selectedTab === "Custom"
            ? customRangeDurationData
            : monthlyDurationData;

    // Only include data points that have actual values
    const validDataPoints = data
      .filter((d) => d.duration > 0)
      .map((d) => ({
        x: d.dateLabel,
        y: d.duration,
      }));

    // Calculate trendline
    const { trendlineData, percentChange } =
      calculateTrendline(validDataPoints);

    const datasets = [
      {
        id: "Sleep Duration",
        data: validDataPoints,
      },
    ];

    // Add trendline if available
    if (trendlineData.length > 0) {
      datasets.push({
        id: "Sleep Duration Trendline",
        data: trendlineData,
      } as any);
    }

    return { chartData: datasets, trendlinePercentChange: percentChange };
  }, [
    selectedTab,
    sleepDurationData,
    monthlyDurationData,
    quarterlyDurationData,
    customRangeDurationData,
  ]);

  // Calculate min/max for sleep duration chart y-axis
  const sleepDurationYAxisRange = useMemo(() => {
    if (
      !transformSleepDurationDataForLineChart ||
      transformSleepDurationDataForLineChart.chartData.length === 0 ||
      !transformSleepDurationDataForLineChart.chartData[0] ||
      !transformSleepDurationDataForLineChart.chartData[0].data ||
      transformSleepDurationDataForLineChart.chartData[0].data.length === 0
    ) {
      return { min: 0, max: "auto" as const };
    }

    const values = transformSleepDurationDataForLineChart.chartData[0].data.map(
      (d: any) => d.y,
    );
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);

    // Set min to 25% less than the lowest value, but not below 0
    const calculatedMin = Math.max(0, minValue * 0.75);

    return { min: calculatedMin, max: "auto" as const };
  }, [transformSleepDurationDataForLineChart]);

  const transformSleepScoreDataForLineChart = useMemo(() => {
    if (selectedTab === "Day")
      return { chartData: [], trendlinePercentChange: 0 };

    const data =
      selectedTab === "Week"
        ? sleepData
        : selectedTab === "Quarter"
          ? quarterlySleepData
          : selectedTab === "Custom"
            ? customRangeSleepData
            : monthlySleepData;

    // Only include data points that have actual values
    const validDataPoints = data
      .filter((d) => d.hasData && d.score > 0)
      .map((d) => ({
        x: d.dateLabel,
        y: d.score,
      }));

    // Calculate trendline
    const { trendlineData, percentChange } =
      calculateTrendline(validDataPoints);

    const datasets = [
      {
        id: "HLR Score",
        data: validDataPoints,
      },
    ];

    // Add trendline if available
    if (trendlineData.length > 0) {
      datasets.push({
        id: "HLR Score Trendline",
        data: trendlineData,
      } as any);
    }

    return { chartData: datasets, trendlinePercentChange: percentChange };
  }, [
    selectedTab,
    sleepData,
    monthlySleepData,
    quarterlySleepData,
    customRangeSleepData,
  ]);

  // Transform health metrics data for combined chart with scaled respiratory rate
  const transformHealthMetricsDataForLineChart = useMemo(() => {
    if (selectedTab === "Day") return [];

    const data =
      selectedTab === "Week"
        ? healthMetricsData
        : selectedTab === "Quarter"
          ? quarterlyHealthMetricsData
          : selectedTab === "Custom"
            ? customRangeHealthMetricsData
            : monthlyHealthMetricsData;

    // Create separate datasets for each metric (no pre-filtering needed)
    const hrvData = data
      .filter((d) => d.hrv && d.hrv > 0)
      .map((d) => ({
        x: d.dateLabel,
        y: d.hrv!,
      }));

    const rhrData = data
      .filter((d) => d.rhr && d.rhr > 0)
      .map((d) => ({
        x: d.dateLabel,
        y: d.rhr!,
      }));

    // Scale respiratory rate to be visually comparable (multiply by ~4 to put in 40-100 range)
    const respRateData = data
      .filter((d) => d.respRate && d.respRate > 0)
      .map((d) => ({
        x: d.dateLabel,
        y: d.respRate! * 4, // Scale respiratory rate for visual comparison
        originalValue: d.respRate!, // Keep original value for tooltip
      }));

    const datasets = [
      {
        id: "HRV",
        data: hrvData,
      },
      {
        id: "RHR",
        data: rhrData,
      },
      {
        id: "Respiratory Rate (Scaled)",
        data: respRateData,
      },
    ].filter((dataset) => dataset.data.length > 0); // Only include datasets with data

    // Add trendlines for both Week and Month views
    const trendlines = datasets
      .map((dataset) => {
        const { trendlineData, percentChange } = calculateTrendline(
          dataset.data,
        );
        return {
          id: `${dataset.id} Trendline`,
          data: trendlineData,
          percentChange,
        };
      })
      .filter((trendline) => trendline.data.length > 0);

    return [...datasets, ...trendlines];
  }, [
    selectedTab,
    healthMetricsData,
    monthlyHealthMetricsData,
    quarterlyHealthMetricsData,
    customRangeHealthMetricsData,
  ]);

  // Handle back navigation - return to dashboard with the same date
  const handleBack = () => {
    const dateID = generateDateID(targetDate);
    // navigate(`/dashboard?date=${dateID}`);
    navigate(-1);
  };
  const getLongestHealthLog = (healthLogs: any[]): any | null => {
    if (!healthLogs || healthLogs.length === 0) return null;
    return healthLogs.reduce((max, log) =>
      (log.sleepDuration || 0) > (max.sleepDuration || 0) ? log : max
    );
  };
  // console.log("the current day health is ", currentDayHealth);
  return (
    <div className="w-full min-h-screen bg-base-200 pt-16">
      <Header />
      {showPremiumModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-20 p-4">
          <div className="relative">
            <button
              onClick={() => setShowPremiumModal(false)}
              className="absolute -top-3 -right-3 bg-base-100 rounded-full px-2 py-1 text-sm"
            >
              ✕
            </button>

            <ComingSoon featureKey="advancedGraphs" />
          </div>
        </div>
      )}
      {/* Header */}
      <div className=" shadow-sm">
        <div className="mx-auto max-w-4xl px-4 py-4">
          <div className="flex w-full items-center justify-between">
            <div className="flex w-full items-center justify-between">
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleBack}
                  className="p-2 rounded-full hover:bg-base-200 transition-colors bg-base-300 "
                >
                  <ChevronLeftIcon className="h-5 w-5 text-base-content/80" />
                </button>
                <h1 className="text-2xl font-grotesk font-bold text-base-content">
                  Health and Sleep
                </h1>
              </div>
              <div className="flex space-x-2">
                {/* Export button hidden - feature not available yet */}
                {/* <button
                  onClick={() =>
                    isPremium
                      ? setShowExportDialog(true)
                      : setShowPremiumModal(true)
                  }
                  className="w-8 h-8 rounded-full bg-base-300 hover:bg-base-300 flex items-center justify-center text-base-content/70 hover:text-base-content transition-colors"
                  title="Export wellness data"
                >
                  <ArrowDownTrayIcon
                    className={`h-4 w-4 ${
                      isPremium ? "text-base-content/60" : "text-blue-500"
                    }`}
                  />
                </button> */}
                <button
                  onClick={() => setShowInfoDialog(true)}
                  className="w-8 h-8 rounded-full bg-base-300 hover:bg-base-300 flex items-center justify-center text-base-content/70 hover:text-base-content transition-colors"
                  title="Learn more about Health and Sleep"
                >
                  ?
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      {/* Tab Navigation */}
      <div className="mx-auto max-w-4xl">
        <div className="flex justify-around border-b border-base-300">
          {(["Day", "Week", "Month", "Quarter"] as const).map((tab) => {
            // Determine if this tab is restricted for the current user
            const isRestricted =
              (tab === "Month" && !hasMonthlyAccess) ||
              (tab === "Quarter" && !hasQuarterlyAccess);

            return (
              <button
                key={tab}
                onClick={() => {
                  if (isRestricted) {
                    setShowPremiumModal(true);
                    return;
                  }
                  setSelectedTab(tab);
                }}
                className={`relative py-3 px-4 text-sm font-medium font-grotesk transition-colors duration-200 flex-1 text-center flex items-center justify-center gap-1 ${
                  selectedTab === tab
                    ? "text-base-content border-b-2 border-base-content bg-base-300"
                    : "text-base-content/60 hover:text-base-content/80"
                }`}
              >
                {tab}
                {isRestricted && (
                  <span className="text-blue-500">
                    <LockClosedIcon className="w-3 h-3" />
                  </span>
                )}
              </button>
            );
          })}
          {/* Custom Date Range Icon Button */}
          <button
            onClick={() =>
              hasCustomDateAccess
                ? setShowCustomDateModal(true)
                : setShowPremiumModal(true)
            }
            className={`relative py-3 px-4 text-sm font-medium font-grotesk transition-colors duration-200 text-center flex items-center justify-center gap-1 ${
              selectedTab === "Custom"
                ? "text-base-content border-b-2 border-base-content bg-base-300"
                : "text-base-content/60 hover:text-base-content/80"
            }`}
            title="Custom Date Range"
          >
            <CalendarIcon
              className={`h-5 w-5 ${
                hasCustomDateAccess ? "text-base-content/60" : "text-blue-500"
              }`}
            />
            {!hasCustomDateAccess && (
              <span className="text-blue-500">
                <LockClosedIcon className="w-3 h-3" />
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="mx-auto max-w-4xl">
          <div className="flex flex-col items-center justify-center py-20">
            <div className="flex flex-col items-center gap-6">
              {/* Large animated chevron */}
              <h1 className="text-base-content text-[8rem] font-groteskLogo animate-pulse-custom relative z-10">
                ^
              </h1>

              {/* Loading text */}
              <div className="text-center -mt-16">
                <p className="text-lg font-medium text-base-content font-grotesk">
                  Loading Health & Sleep data...
                </p>
                <p className="text-sm text-base-content/70 font-grotesk mt-2">
                  Analyzing your sleep metrics
                </p>
              </div>

              {/* Loading progress indicator */}
              <div className="flex items-center gap-2 mt-4">
                <div className="w-32 h-1 bg-base-300 rounded-full overflow-hidden">
                  <div className="h-full bg-purple-500 rounded-full animate-pulse transition-all duration-1000 w-3/4"></div>
                </div>
                <div className="w-2 h-2 bg-purple-500 rounded-full animate-ping"></div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Content */}
          <div className="mx-auto max-w-4xl">
            {error && (
              <div className="bg-red-500/10 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {/* <h2 className="text-xl font-heading font-semibold text-base-content mb-4 px-4">
          {selectedTab === "Day" && "Today's Health & Sleep Breakdown"}
          {selectedTab === "Week" && "Last 7 Days"}
          {selectedTab === "Month" && "Last 28 Days"}
        </h2> */}

            {/* Chart */}
            <div className="p-6 relative">
              {((!hasMonthlyAccess && selectedTab === "Month") ||
                (!hasQuarterlyAccess && selectedTab === "Quarter")) && (
                <div className="absolute inset-0 z-10 flex items-start justify-center pt-8 px-4">
                  <ComingSoon featureKey="advancedGraphs" />
                </div>
              )}
              <div
                className={
                  (!hasMonthlyAccess && selectedTab === "Month") ||
                  (!hasQuarterlyAccess && selectedTab === "Quarter")
                    ? "blur-md pointer-events-none"
                    : ""
                }
              >
                {selectedTab === "Day" ? (
                  // Day view - detailed health breakdown
                  currentDayHealth ? (
                    <div className="space-y-6">
                      {/* HLR Display */}
                      {currentDayHLR !== null &&
                        currentDayHLR !== undefined && (
                          <div className="mx-4 mt-8 relative">
                            {!isPlusOrHigher && (
                              <div className="absolute inset-0 z-10 flex items-center justify-center">
                                <ComingSoon featureKey="heartLungRecovery" />
                              </div>
                            )}
                            <div
                              className={
                                !isPlusOrHigher
                                  ? "blur-md pointer-events-none"
                                  : ""
                              }
                            >
                              <div className="flex justify-between pb-4">
                                <div className="font-grotesk mb-4">
                                  <h1 className="text-3xl">
                                    <strong>HLR</strong>
                                  </h1>
                                  <p>Heart Lung Recovery Index</p>
                                  <p className="text-xs text-base-content/70">
                                    How well your cardiorespiratory system is
                                    adapting
                                  </p>
                                </div>
                                <div className="flex justify-between items-start">
                                  <span className="text-2xl font-bold">
                                    {(() => {
                                      const diff = Math.round(
                                        currentDayHLR * 100 - 100,
                                      );
                                      return `${diff > 0 ? "+" : ""}${diff}%`;
                                    })()}
                                  </span>
                                </div>
                              </div>
                              <div className=" bg-base-100 rounded-lg shadow-sm p-4">
                                <div className="text-sm text-base-content/80">
                                  {(() => {
                                    const hlr = currentDayHLR;
                                    if (hlr > 1.07) {
                                      return (
                                        <>
                                          <p className="font-bold font-grotesk text-base-content mb-1 text-lg">
                                            Well Above Normal
                                          </p>
                                          <p>
                                            Trend shows strong physiological
                                            recovery signals, consistent with
                                            positive adaptation across heart,
                                            lung, and autonomic systems.
                                          </p>
                                        </>
                                      );
                                    } else if (hlr >= 1.03 && hlr <= 1.07) {
                                      return (
                                        <>
                                          <p className="font-bold font-grotesk text-base-content mb-1 text-lg">
                                            Above Normal
                                          </p>
                                          <p>
                                            Trend reflects robust recovery
                                            patterns, with markers reliably
                                            above baseline.
                                          </p>
                                        </>
                                      );
                                    } else if (hlr >= 1.0 && hlr < 1.03) {
                                      return (
                                        <>
                                          <p className="font-bold font-grotesk text-base-content mb-1 text-lg">
                                            Normal Optimal
                                          </p>
                                          <p>
                                            Trend shows stable physiological
                                            recovery, with heart and lung
                                            markers at or slightly above
                                            baseline.
                                          </p>
                                        </>
                                      );
                                    } else if (hlr >= 0.97 && hlr < 1.0) {
                                      return (
                                        <>
                                          <p className="font-bold font-grotesk text-base-content mb-1 text-lg">
                                            Normal Stable
                                          </p>
                                          <p>
                                            Trend reflects a steady and balanced
                                            recovery state within expected day
                                            to day variation.
                                          </p>
                                        </>
                                      );
                                    } else if (hlr >= 0.93 && hlr < 0.97) {
                                      return (
                                        <>
                                          <p className="font-bold font-grotesk text-base-content mb-1 text-lg">
                                            Below Normal Caution
                                          </p>
                                          <p>
                                            Trend shows early physiological
                                            signs of reduced recovery, with
                                            subtle downward shifts in heart and
                                            lung markers.
                                          </p>
                                        </>
                                      );
                                    } else {
                                      return (
                                        <>
                                          <p className="font-bold font-grotesk text-base-content mb-1 text-lg">
                                            Suppressed Recovery
                                          </p>
                                          <p>
                                            Trend shows clear signs of under
                                            recovery, with markers meaningfully
                                            below baseline. This pattern may
                                            also be associated with an increased
                                            risk of illness.
                                          </p>
                                        </>
                                      );
                                    }
                                  })()}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                      {/* Sleep Timeline - Collapsible */}
                      <div>
                        {/* Collapsible Header */}
                        <div
                          onClick={() =>
                            setIsSleepTimelineExpanded(!isSleepTimelineExpanded)
                          }
                          className="flex items-center justify-between p-4 cursor-pointer hover:bg-purple-500/15 transition-colors rounded-md mb-2 bg-purple-500/10 mx-4 mt-4"
                        >
                          <div className="flex flex-col">
                            <h3 className="text-md font-bold font-grotesk text-base-content ">
                              Sleep Timeline
                            </h3>
                            {/* {currentDayHealth.sleepDuration && (
                              <p className="text-sm text-purple-600 font-bold mt-1">
                                {(() => {
                                  const totalSeconds = calcSleepFromTimeline(
                                    currentDayHealth.sleepTimeline,
                                    currentDayHealth.sleepDuration,
                                  );
                                  const hours = Math.floor(totalSeconds / 3600);
                                  const minutes = Math.round(
                                    (totalSeconds % 3600) / 60,
                                  );
                                  return `${hours}h ${minutes}min`;
                                })()}
                              </p>
                            )} */}
                            {/* commented above code as now we will target sleep duration  */}
                            {typeof currentDayHealth?.sleepDuration ===
                              "number" &&
                              currentDayHealth.sleepDuration > 0 && (
                                <p className="text-sm text-purple-600 font-bold mt-1">
                                  {(() => {
                                    const totalSeconds = Math.max(
                                      0,
                                      Math.floor(
                                        currentDayHealth.sleepDuration,
                                      ),
                                    );

                                    const hours = Math.floor(
                                      totalSeconds / 3600,
                                    );
                                    const minutes = Math.round(
                                      (totalSeconds % 3600) / 60,
                                    );

                                    return `${hours}h ${minutes}min`;
                                  })()}
                                </p>
                              )}
                          </div>
                          {isSleepTimelineExpanded ? (
                            <ChevronUpIcon className="h-4 w-4 text-base-content/70" />
                          ) : (
                            <ChevronDownIcon className="h-4 w-4 text-base-content/70" />
                          )}
                        </div>

                        {/* Collapsible Content */}
                        {isSleepTimelineExpanded && (
                          <SleepTimeline
                            dateID={generateDateID(targetDate)}
                            className="rounded-lg p-4"
                            sleepTimeline={currentDayHealth.sleepTimeline}
                            currentDay={currentDayHealth}
                          />
                        )}
                      </div>

                      {/* Health Stats */}
                      <RecoveryCard
                        latestHealthLog={
                          currentDayHealth
                            ? { ...currentDayHealth, id: "current" }
                            : null
                        }
                        hlrScore={currentDayHLR}
                        rhrPercentDiff={healthMetricsComparison.rhrPercentDiff}
                        hrvPercentDiff={healthMetricsComparison.hrvPercentDiff}
                        respPercentDiff={
                          healthMetricsComparison.respPercentDiff
                        }
                        loading28DayData={false}
                      />
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-16 px-4">
                      <div className="text-center max-w-md">
                        <h3 className="text-lg font-grotesk font-semibold text-base-content mb-2">
                          No Data Available
                        </h3>
                        <p className="text-sm text-base-content/70 font-grotesk">
                          No health or sleep data has been logged for this day
                          yet.
                        </p>
                      </div>
                    </div>
                  )
                ) : // Week, Month, Quarter, and Custom view - charts
                (selectedTab === "Week" && sleepData.some((d) => d.hasData)) ||
                  (selectedTab === "Month" &&
                    monthlySleepData.some((d) => d.hasData)) ||
                  (selectedTab === "Quarter" &&
                    quarterlySleepData.some((d) => d.hasData)) ||
                  (selectedTab === "Custom" &&
                    customRangeSleepData.some((d) => d.hasData)) ||
                  // Also check duration data separately (can have duration without HLR score)
                  (selectedTab === "Week" &&
                    sleepDurationData.some((d) => d.hasData)) ||
                  (selectedTab === "Month" &&
                    monthlyDurationData.some((d) => d.hasData)) ||
                  (selectedTab === "Quarter" &&
                    quarterlyDurationData.some((d) => d.hasData)) ||
                  (selectedTab === "Custom" &&
                    customRangeDurationData.some((d) => d.hasData)) ||
                  // Also check health metrics data
                  (selectedTab === "Week" &&
                    healthMetricsData.some((d) => d.hasData)) ||
                  (selectedTab === "Month" &&
                    monthlyHealthMetricsData.some((d) => d.hasData)) ||
                  (selectedTab === "Quarter" &&
                    quarterlyHealthMetricsData.some((d) => d.hasData)) ||
                  (selectedTab === "Custom" &&
                    customRangeHealthMetricsData.some((d) => d.hasData)) ? (
                  <div className="space-y-8">
                    <div>
                      {/* Sleep Duration Line Chart */}
                      {transformSleepDurationDataForLineChart.chartData[0]?.data
                        .length > 0 ? (
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <h3 className="text-lg font-heading font-semibold text-base-content">
                                Sleep Duration{" "}
                                <span className="font-grotesk text-base-content/60">
                                  Trend
                                </span>
                              </h3>
                              <p className="text-xs text-base-content/70">
                                {derivedDataAttribution(hasActiveGarminDevice)}
                              </p>
                            </div>
                            {transformSleepDurationDataForLineChart.trendlinePercentChange !==
                              0 && (
                              <div
                                className={`text-sm font-medium mr-4 ${
                                  transformSleepDurationDataForLineChart.trendlinePercentChange >
                                  0
                                    ? "text-green-600"
                                    : transformSleepDurationDataForLineChart.trendlinePercentChange <
                                        0
                                      ? "text-red-600"
                                      : "text-base-content/70"
                                }`}
                              >
                                {transformSleepDurationDataForLineChart.trendlinePercentChange >
                                0
                                  ? "↗"
                                  : transformSleepDurationDataForLineChart.trendlinePercentChange <
                                      0
                                    ? "↘"
                                    : "→"}{" "}
                                {Math.abs(
                                  transformSleepDurationDataForLineChart.trendlinePercentChange,
                                ).toFixed(1)}
                                %
                              </div>
                            )}
                          </div>
                          <div className="h-80 w-full">
                            <ResponsiveLine
                              theme={nivoTheme}
                              data={
                                transformSleepDurationDataForLineChart.chartData
                              }
                              margin={{
                                top: 40,
                                right: 20,
                                bottom: 80,
                                left: 50,
                              }}
                              xScale={{ type: "point" }}
                              yScale={{
                                type: "linear",
                                min: sleepDurationYAxisRange.min,
                                max: sleepDurationYAxisRange.max,
                                stacked: false,
                              }}
                              curve="cardinal"
                              enablePoints={false}
                              axisTop={null}
                              axisRight={null}
                              axisBottom={{
                                tickSize: 5,
                                tickPadding: 5,
                                tickRotation: -45,
                                legend: "",
                                legendPosition: "middle",
                                legendOffset: 50,
                                // Show selective tick values on monthly and quarterly view
                                tickValues:
                                  selectedTab === "Month"
                                    ? transformSleepDurationDataForLineChart.chartData[0]?.data
                                        .filter((_, i) => i % 7 === 0)
                                        .map((d) => d.x)
                                    : selectedTab === "Quarter"
                                      ? transformSleepDurationDataForLineChart.chartData[0]?.data
                                          .filter((_, i) => i % 3 === 0)
                                          .map((d) => d.x)
                                      : undefined,
                              }}
                              axisLeft={{
                                tickSize: 0,
                                tickPadding: 10,
                                tickRotation: 0,
                                legend: "",
                                legendPosition: "middle",
                                legendOffset: -40,
                                tickValues: 5,
                                format: (value: any) =>
                                  `${Math.round(value * 10) / 10}h`,
                              }}
                              enableGridX={false}
                              enableGridY={true}
                              colors={["#9333ea", "#9ca3af"]}
                              pointSize={8}
                              pointColor={{ from: "color" }}
                              pointBorderWidth={2}
                              pointBorderColor={isDark ? "#1a1a2e" : "#ffffff"}
                              enablePointLabel={false}
                              enableArea={true}
                              areaOpacity={0.2}
                              areaBaselineValue={sleepDurationYAxisRange.min}
                              fill={[
                                {
                                  match: { id: "Sleep Duration" },
                                  id: "area",
                                },
                              ]}
                              useMesh={true}
                              layers={[
                                "grid",
                                "markers",
                                "axes",
                                "areas",
                                "crosshair",
                                "lines",
                                "slices",
                                "mesh",
                                "legends",
                                // Custom points layer to control colors per series
                                (props: any) => {
                                  return props.points.map((point: any) => {
                                    // Check if this is the trendline series
                                    const isTrendline =
                                      point.serieId?.includes("Trendline") ||
                                      point.id?.includes("Trendline");
                                    const color = isTrendline
                                      ? "#9ca3af"
                                      : "#9333ea";
                                    return (
                                      <circle
                                        key={point.id}
                                        cx={point.x}
                                        cy={point.y}
                                        r={3}
                                        fill={color}
                                        stroke={isDark ? "#1a1a2e" : "#ffffff"}
                                        strokeWidth={1.5}
                                      />
                                    );
                                  });
                                },
                              ]}
                              tooltip={(point: any) => (
                                <div className="bg-base-100 p-3 shadow-lg rounded-lg border min-w-[200px]">
                                  <div className="font-medium">
                                    {point.point.data.x}
                                  </div>
                                  <div className="text-sm text-base-content/70">
                                    Duration:{" "}
                                    {Math.round(point.point.data.y * 10) / 10}h
                                  </div>
                                </div>
                              )}
                              animate={true}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-8 px-4">
                          <p className="text-sm text-base-content/50 font-grotesk">
                            No sleep duration data available yet
                          </p>
                        </div>
                      )}

                      {/* Health Score (HLR) Line Chart */}
                      {transformSleepScoreDataForLineChart.chartData[0]?.data
                        .length > 0 ? (
                        <div className="mt-8 relative">
                          {!isPlusOrHigher && (
                            <div className="absolute inset-0 z-10 flex items-center justify-center">
                              <ComingSoon featureKey="heartLungRecovery" />
                            </div>
                          )}
                          <div
                            className={
                              !isPlusOrHigher
                                ? "blur-md pointer-events-none"
                                : ""
                            }
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div>
                                <h3 className="text-lg font-heading font-semibold text-base-content">
                                  Heart Lung Recovery (HLR){" "}
                                  <span className="font-grotesk text-base-content/60">
                                    Index
                                  </span>{" "}
                                </h3>
                                <p className="text-xs text-base-content/70">
                                  How well your cardiorespiratory system is
                                  adapting to training and recovery.
                                </p>
                                <p className="text-xs text-base-content/50 mt-0.5">
                                  {derivedDataAttribution(hasActiveGarminDevice)}
                                </p>
                              </div>
                            </div>
                            {/* Main Score Display */}
                            <div className="p-2 py-4 w-full bg-base-100 rounded-md mb-4">
                              <div className="w-full px-4">
                                <div className="flex justify-between items-center">
                                  <div className="flex items-center">
                                    <div className="flex justify-between mr-2">
                                      <div className="text-xl font-heading font-bold text-base-content">
                                        {(() => {
                                          const diff = Math.round(
                                            averageScore * 100 - 100,
                                          );
                                          return `${diff > 0 ? "+" : ""}${diff}%`;
                                        })()}
                                      </div>
                                    </div>
                                    <div className="text-sm text-base-content/70 font-grotesk">
                                      {selectedTab === "Week" &&
                                        "Last 7 days Average"}
                                      {selectedTab === "Month" &&
                                        "Last 28 days Average"}
                                      {selectedTab === "Quarter" &&
                                        "Last 12 weeks Average"}
                                    </div>
                                  </div>
                                  <div className="flex gap-2 items-center">
                                    {transformSleepScoreDataForLineChart.trendlinePercentChange !==
                                      0 && (
                                      <div
                                        className={`text-sm font-medium ${
                                          transformSleepScoreDataForLineChart.trendlinePercentChange >
                                          0
                                            ? "text-green-600"
                                            : transformSleepScoreDataForLineChart.trendlinePercentChange <
                                                0
                                              ? "text-red-600"
                                              : "text-base-content/70"
                                        }`}
                                      >
                                        {transformSleepScoreDataForLineChart.trendlinePercentChange >
                                        0
                                          ? "↗"
                                          : transformSleepScoreDataForLineChart.trendlinePercentChange <
                                              0
                                            ? "↘"
                                            : "→"}{" "}
                                        {Math.abs(
                                          transformSleepScoreDataForLineChart.trendlinePercentChange,
                                        ).toFixed(1)}
                                        %
                                      </div>
                                    )}
                                  </div>
                                </div>

                                <p className="text-sm text-base-content">
                                  {averageScore > 1.07
                                    ? "Well above normal. Trend shows strong physiological recovery signals, consistent with positive adaptation across heart, lung, and autonomic systems."
                                    : averageScore >= 1.03
                                      ? "Above normal. Trend reflects robust recovery patterns, with markers reliably above baseline."
                                      : averageScore >= 1.0
                                        ? "Normal optimal. Trend shows stable physiological recovery, with heart and lung markers at or slightly above baseline."
                                        : averageScore >= 0.97
                                          ? "Normal stable. Trend reflects a steady and balanced recovery state within expected day to day variation."
                                          : averageScore >= 0.93
                                            ? "Below normal caution. Trend shows early physiological signs of reduced recovery, with subtle downward shifts in heart and lung markers."
                                            : "Suppressed recovery. Trend shows clear signs of under recovery, with markers meaningfully below baseline. This pattern may also be associated with an increased risk of illness."}
                                </p>
                              </div>
                            </div>
                            <div className="h-80 w-full">
                              <ResponsiveLine
                                theme={nivoTheme}
                                data={
                                  transformSleepScoreDataForLineChart.chartData
                                }
                                margin={{
                                  top: 40,
                                  right: 20,
                                  bottom: 100,
                                  left: 50,
                                }}
                                xScale={{ type: "point" }}
                                yScale={{
                                  type: "linear",
                                  min: 0.8,
                                  max: 1.2,
                                  stacked: false,
                                }}
                                curve="cardinal"
                                enablePoints={false}
                                axisTop={null}
                                axisRight={null}
                                axisBottom={{
                                  tickSize: 5,
                                  tickPadding: 5,
                                  tickRotation: -45,
                                  legend: "",
                                  legendPosition: "middle",
                                  legendOffset: 50,
                                  // Show selective tick values on monthly and quarterly view
                                  tickValues:
                                    selectedTab === "Month"
                                      ? transformSleepScoreDataForLineChart.chartData[0]?.data
                                          .filter((_, i) => i % 7 === 0)
                                          .map((d) => d.x)
                                      : selectedTab === "Quarter"
                                        ? transformSleepScoreDataForLineChart.chartData[0]?.data
                                            .filter((_, i) => i % 3 === 0)
                                            .map((d) => d.x)
                                        : undefined,
                                }}
                                axisLeft={{
                                  tickSize: 0,
                                  tickPadding: 10,
                                  tickRotation: 0,
                                  legend: "",
                                  legendPosition: "middle",
                                  legendOffset: -40,
                                  tickValues: 5,
                                  format: (value: any) => {
                                    const diff = Math.round(value * 100 - 100);
                                    return `${diff > 0 ? "+" : ""}${diff}%`;
                                  },
                                }}
                                enableGridX={false}
                                enableGridY={true}
                                colors={["#581c87", "#9ca3af"]}
                                pointSize={8}
                                pointColor={{ from: "color" }}
                                pointBorderWidth={2}
                                pointBorderColor={
                                  isDark ? "#1a1a2e" : "#ffffff"
                                }
                                enablePointLabel={false}
                                enableArea={false}
                                useMesh={true}
                                layers={[
                                  // Background color bands for HLR ranges
                                  (props: any) => {
                                    const { innerWidth, innerHeight } = props;
                                    const yMin = 0.8;
                                    const yMax = 1.2;
                                    const range = yMax - yMin;
                                    const bands = [
                                      {
                                        from: 1.1,
                                        to: 1.2,
                                        color: isDark
                                          ? "rgba(34,197,94,0.15)"
                                          : "rgba(34,197,94,0.12)",
                                      },
                                      {
                                        from: 1.0,
                                        to: 1.1,
                                        color: isDark
                                          ? "rgba(134,239,172,0.12)"
                                          : "rgba(134,239,172,0.10)",
                                      },
                                      {
                                        from: 0.9,
                                        to: 1.0,
                                        color: isDark
                                          ? "rgba(252,165,165,0.12)"
                                          : "rgba(252,165,165,0.10)",
                                      },
                                      {
                                        from: 0.8,
                                        to: 0.9,
                                        color: isDark
                                          ? "rgba(239,68,68,0.15)"
                                          : "rgba(239,68,68,0.10)",
                                      },
                                    ];
                                    return (
                                      <g>
                                        {bands.map((band, i) => {
                                          const y =
                                            ((yMax - band.to) / range) *
                                            innerHeight;
                                          const h =
                                            ((band.to - band.from) / range) *
                                            innerHeight;
                                          return (
                                            <rect
                                              key={i}
                                              x={0}
                                              y={y}
                                              width={innerWidth}
                                              height={h}
                                              fill={band.color}
                                            />
                                          );
                                        })}
                                      </g>
                                    );
                                  },
                                  "grid",
                                  "markers",
                                  "axes",
                                  "areas",
                                  "crosshair",
                                  "lines",
                                  "slices",
                                  "mesh",
                                  "legends",
                                  // Custom points layer to control colors per series
                                  (props: any) => {
                                    return props.points.map((point: any) => {
                                      // Check if this is the trendline series
                                      const isTrendline =
                                        point.serieId?.includes("Trendline") ||
                                        point.id?.includes("Trendline");
                                      const color = isTrendline
                                        ? "#9ca3af"
                                        : "#581c87";
                                      return (
                                        <circle
                                          key={point.id}
                                          cx={point.x}
                                          cy={point.y}
                                          r={3}
                                          fill={color}
                                          stroke={
                                            isDark ? "#1a1a2e" : "#ffffff"
                                          }
                                          strokeWidth={1.5}
                                        />
                                      );
                                    });
                                  },
                                ]}
                                tooltip={(point: any) => (
                                  <div className="bg-base-100 p-3 shadow-lg rounded-lg border min-w-[200px]">
                                    <div className="font-medium">
                                      {point.point.data.x}
                                    </div>
                                    <div className="text-sm text-base-content/70">
                                      HLR:{" "}
                                      {(() => {
                                        const diff = Math.round(
                                          Number(point.point.data.y) * 100 -
                                            100,
                                        );
                                        return `${diff > 0 ? "+" : ""}${diff}%`;
                                      })()}
                                    </div>
                                  </div>
                                )}
                                animate={true}
                              />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-8 px-4">
                          <p className="text-sm text-base-content/50 font-grotesk">
                            No HLR data available yet
                          </p>
                        </div>
                      )}

                      {/* Health Metrics Individual Charts */}
                      {transformHealthMetricsDataForLineChart.length > 0 ? (
                        <div className="mt-8">
                          <h3 className="text-lg font-heading font-semibold text-base-content">
                            Health Metrics Trend
                          </h3>
                          <p className="text-xs text-base-content/70 mb-6">
                            {derivedDataAttribution(hasActiveGarminDevice)}
                          </p>

                          <div className="space-y-1">
                            {/* HRV Chart */}
                            {(() => {
                              const hrvData =
                                transformHealthMetricsDataForLineChart.find(
                                  (d) => d.id === "HRV",
                                );
                              const hrvTrendline =
                                transformHealthMetricsDataForLineChart.find(
                                  (d) => d.id === "HRV Trendline",
                                );

                              const chartData = [hrvData, hrvTrendline].filter(
                                (
                                  d,
                                ): d is {
                                  id: string;
                                  data: { x: string; y: number }[];
                                  percentChange?: number;
                                } => Boolean(d),
                              );

                              return hrvData && hrvData.data.length > 0 ? (
                                <div className="rounded-lg">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                      <h4 className="text-sm font-bold font-grotesk text-base-content">
                                        Heart Rate Variability (HRV)
                                      </h4>
                                      <span className="text-xs text-base-content/60">
                                        milliseconds
                                      </span>
                                    </div>
                                    {hrvTrendline &&
                                      (hrvTrendline as any).percentChange !==
                                        undefined && (
                                        <div
                                          className={`text-sm font-medium mr-4 ${
                                            (hrvTrendline as any)
                                              .percentChange > 0
                                              ? "text-green-600"
                                              : (hrvTrendline as any)
                                                    .percentChange < 0
                                                ? "text-red-600"
                                                : "text-base-content/70"
                                          }`}
                                        >
                                          {(hrvTrendline as any).percentChange >
                                          0
                                            ? "↗"
                                            : (hrvTrendline as any)
                                                  .percentChange < 0
                                              ? "↘"
                                              : "→"}{" "}
                                          {Math.abs(
                                            (hrvTrendline as any).percentChange,
                                          ).toFixed(1)}
                                          %
                                        </div>
                                      )}
                                  </div>
                                  <div className="h-24 w-full">
                                    <ResponsiveLine
                                      theme={nivoTheme}
                                      data={chartData}
                                      margin={{
                                        top: 10,
                                        right: 20,
                                        bottom: 10,
                                        left: 40,
                                      }}
                                      xScale={{ type: "point" }}
                                      yScale={{
                                        type: "linear",
                                        min: "auto",
                                        max: "auto",
                                        stacked: false,
                                      }}
                                      curve="cardinal"
                                      enablePoints={true}
                                      axisTop={null}
                                      axisRight={null}
                                      axisBottom={null}
                                      axisLeft={{
                                        tickSize: 0,
                                        tickPadding: 5,
                                        tickValues: 3,
                                        format: (value: any) =>
                                          `${Math.round(value)}`,
                                      }}
                                      enableGridX={false}
                                      enableGridY={true}
                                      colors={[
                                        isDark ? "#e5e5e5" : "#374151",
                                        "#9ca3af",
                                      ]}
                                      pointSize={4}
                                      pointBorderWidth={1}
                                      pointBorderColor={
                                        isDark ? "#1a1a2e" : "#ffffff"
                                      }
                                      enablePointLabel={false}
                                      enableArea={true}
                                      areaOpacity={0.08}
                                      useMesh={true}
                                      tooltip={(point: any) => (
                                        <div className="bg-base-100 p-2 shadow-lg rounded border text-xs min-w-[200px]">
                                          <div className="font-medium">
                                            {point.point.data.x}
                                          </div>
                                          <div className="text-red-800 dark:text-red-300">
                                            HRV:{" "}
                                            {Math.round(point.point.data.y)} ms
                                          </div>
                                        </div>
                                      )}
                                      animate={true}
                                    />
                                  </div>
                                </div>
                              ) : null;
                            })()}

                            {/* RHR Chart */}
                            {(() => {
                              const rhrData =
                                transformHealthMetricsDataForLineChart.find(
                                  (d) => d.id === "RHR",
                                );
                              const rhrTrendline =
                                transformHealthMetricsDataForLineChart.find(
                                  (d) => d.id === "RHR Trendline",
                                );

                              const chartData = [rhrData, rhrTrendline].filter(
                                (
                                  d,
                                ): d is {
                                  id: string;
                                  data: { x: string; y: number }[];
                                  percentChange?: number;
                                } => Boolean(d),
                              );

                              return rhrData && rhrData.data.length > 0 ? (
                                <div className="rounded-lg pt-5">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                      <h4 className="text-sm font-bold font-grotesk text-base-content">
                                        Resting Heart Rate (RHR)
                                      </h4>
                                      <span className="text-xs text-base-content/60">
                                        beats per minute
                                      </span>
                                    </div>
                                    {rhrTrendline &&
                                      (rhrTrendline as any).percentChange !==
                                        undefined && (
                                        <div
                                          className={`text-sm font-medium mr-4  ${
                                            (rhrTrendline as any)
                                              .percentChange < 0
                                              ? "text-green-600"
                                              : (rhrTrendline as any)
                                                    .percentChange > 0
                                                ? "text-red-600"
                                                : "text-base-content/70"
                                          }`}
                                        >
                                          {(rhrTrendline as any).percentChange <
                                          0
                                            ? "↘"
                                            : (rhrTrendline as any)
                                                  .percentChange > 0
                                              ? "↗"
                                              : "→"}{" "}
                                          {Math.abs(
                                            (rhrTrendline as any).percentChange,
                                          ).toFixed(1)}
                                          %
                                        </div>
                                      )}
                                  </div>
                                  <div className="h-24 w-full">
                                    <ResponsiveLine
                                      theme={nivoTheme}
                                      data={chartData}
                                      margin={{
                                        top: 10,
                                        right: 20,
                                        bottom: 10,
                                        left: 40,
                                      }}
                                      xScale={{ type: "point" }}
                                      yScale={{
                                        type: "linear",
                                        min: "auto",
                                        max: "auto",
                                        stacked: false,
                                      }}
                                      curve="cardinal"
                                      enablePoints={true}
                                      axisTop={null}
                                      axisRight={null}
                                      axisBottom={null}
                                      axisLeft={{
                                        tickSize: 0,
                                        tickPadding: 5,
                                        tickValues: 3,
                                        format: (value: any) =>
                                          `${Math.round(value)}`,
                                      }}
                                      enableGridX={false}
                                      enableGridY={true}
                                      colors={[
                                        isDark ? "#e5e5e5" : "#374151",
                                        "#9ca3af",
                                      ]}
                                      pointSize={4}
                                      pointBorderWidth={1}
                                      pointBorderColor={
                                        isDark ? "#1a1a2e" : "#ffffff"
                                      }
                                      enablePointLabel={false}
                                      enableArea={true}
                                      areaOpacity={0.08}
                                      useMesh={true}
                                      tooltip={(point: any) => (
                                        <div className="bg-base-100 p-2 shadow-lg rounded border text-xs min-w-[200px]">
                                          <div className="font-medium">
                                            {point.point.data.x}
                                          </div>
                                          <div className="text-red-600">
                                            RHR:{" "}
                                            {Math.round(point.point.data.y)} bpm
                                          </div>
                                        </div>
                                      )}
                                      animate={true}
                                    />
                                  </div>
                                </div>
                              ) : null;
                            })()}

                            {/* Respiratory Rate Chart */}
                            {(() => {
                              const respData =
                                transformHealthMetricsDataForLineChart.find(
                                  (d) => d.id === "Respiratory Rate (Scaled)",
                                );
                              const respTrendline =
                                transformHealthMetricsDataForLineChart.find(
                                  (d) =>
                                    d.id ===
                                    "Respiratory Rate (Scaled) Trendline",
                                );

                              const respDataForChart = respData
                                ? {
                                    id: "Respiratory Rate",
                                    data: respData.data.map((point: any) => ({
                                      x: point.x,
                                      y: point.originalValue || point.y / 4, // Use original value
                                    })),
                                  }
                                : null;

                              const respTrendlineForChart = respTrendline
                                ? {
                                    id: "Respiratory Rate Trendline",
                                    data: respTrendline.data.map(
                                      (point: any) => ({
                                        x: point.x,
                                        y: point.y / 4, // Convert back to original scale
                                      }),
                                    ),
                                    percentChange: (respTrendline as any)
                                      .percentChange,
                                  }
                                : null;

                              const chartData = [
                                respDataForChart,
                                respTrendlineForChart,
                              ].filter(
                                (
                                  d,
                                ): d is {
                                  id: string;
                                  data: { x: string; y: number }[];
                                  percentChange?: number;
                                } => Boolean(d),
                              );

                              return respData && respData.data.length > 0 ? (
                                <div className="rounded-lg pt-5">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                      <h4 className="text-sm font-bold font-grotesk text-base-content">
                                        Respiratory Rate
                                      </h4>
                                      <span className="text-xs text-base-content/60">
                                        breaths per minute
                                      </span>
                                    </div>
                                    {respTrendlineForChart &&
                                      respTrendlineForChart.percentChange !==
                                        undefined && (
                                        <div
                                          className={`text-sm font-medium mr-4  ${
                                            respTrendlineForChart.percentChange <
                                            0
                                              ? "text-green-600"
                                              : respTrendlineForChart.percentChange >
                                                  0
                                                ? "text-red-600"
                                                : "text-base-content/70"
                                          }`}
                                        >
                                          {respTrendlineForChart.percentChange <
                                          0
                                            ? "↘"
                                            : respTrendlineForChart.percentChange >
                                                0
                                              ? "↗"
                                              : "→"}{" "}
                                          {Math.abs(
                                            respTrendlineForChart.percentChange,
                                          ).toFixed(1)}
                                          %
                                        </div>
                                      )}
                                  </div>
                                  <div className="h-24 w-full">
                                    <ResponsiveLine
                                      theme={nivoTheme}
                                      data={chartData}
                                      margin={{
                                        top: 10,
                                        right: 20,
                                        bottom: 10,
                                        left: 40,
                                      }}
                                      xScale={{ type: "point" }}
                                      yScale={{
                                        type: "linear",
                                        min: "auto",
                                        max: "auto",
                                        stacked: false,
                                      }}
                                      curve="cardinal"
                                      enablePoints={true}
                                      axisTop={null}
                                      axisRight={null}
                                      axisBottom={null}
                                      axisLeft={{
                                        tickSize: 0,
                                        tickPadding: 5,
                                        tickValues: 3,
                                        format: (value: any) =>
                                          `${Math.round(value)}`,
                                      }}
                                      enableGridX={false}
                                      enableGridY={true}
                                      colors={[
                                        isDark ? "#e5e5e5" : "#374151",
                                        "#9ca3af",
                                      ]}
                                      pointSize={4}
                                      pointBorderWidth={1}
                                      pointBorderColor={
                                        isDark ? "#1a1a2e" : "#ffffff"
                                      }
                                      enablePointLabel={false}
                                      enableArea={true}
                                      areaOpacity={0.08}
                                      useMesh={true}
                                      tooltip={(point: any) => (
                                        <div className="bg-base-100 p-2 shadow-lg rounded border text-xs min-w-[200px]">
                                          <div className="font-medium">
                                            {point.point.data.x}
                                          </div>
                                          <div className="text-blue-600">
                                            Respiratory Rate:{" "}
                                            {Math.round(point.point.data.y)}{" "}
                                            /min
                                          </div>
                                        </div>
                                      )}
                                      animate={true}
                                    />
                                  </div>
                                </div>
                              ) : null;
                            })()}
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-8 px-4">
                          <p className="text-sm text-base-content/50 font-grotesk">
                            No health metrics data available yet
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 px-4">
                    <div className="text-center max-w-md">
                      <h3 className="text-lg font-grotesk font-semibold text-base-content mb-2">
                        No Data Available
                      </h3>
                      <p className="text-sm text-base-content/70 font-grotesk">
                        {selectedTab === "Week"
                          ? "No health or sleep data found for the past 7 days."
                          : selectedTab === "Month"
                            ? "No health or sleep data found for the past 30 days."
                            : selectedTab === "Quarter"
                              ? "No health or sleep data found for the past 12 weeks."
                              : "No health or sleep data found for the selected date range."}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Info Dialog */}
          {showInfoDialog && (
            <div className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-md flex items-center justify-center z-50 p-4">
              <div className="bg-base-100 rounded-md max-w-lg w-full max-h-[80vh] overflow-y-auto">
                <div className="p-8">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-grotesk font-semibold text-base-content">
                      Health & Sleep
                    </h3>
                    <button
                      onClick={() => setShowInfoDialog(false)}
                      className="p-1 rounded-full hover:bg-base-200 transition-colors"
                    >
                      <XMarkIcon className="h-5 w-5 text-base-content/60" />
                    </button>
                  </div>

                  <div className="space-y-4 text-sm text-base-content/80">
                    <p className="text-lg font-grotesk">
                      Health & Sleep Data is imported in from your wearable
                      devices.
                    </p>
                    <h4 className="font-bold font-grotesk text-lg text-base-content mb-2">
                      Heart Lung Recovery Index (HLR)
                    </h4>
                    <p>
                      Compares the average of your last 7 days Resting heart
                      rate (RHR), Heart Rate Variability (HRV) and Respiration
                      rate (RR) with your 28 day average to show how well your
                      cardiorespiratory system is adapting to training and
                      recovery over time.
                    </p>

                    <div>
                      <h4 className="font-bold font-grotesk text-lg text-base-content mb-2">
                        What it measures
                      </h4>
                      <ul className="">
                        <li>- Heart Rate Variability (HRV)</li>
                        <li>- Resting Heart Rate (RHR)</li>
                        <li>- Respiratory Rate (RR)</li>
                        <li>- Cardiorespiratory signs of Recovery</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-bold font-grotesk text-lg text-base-content mb-2">
                        HLR Score Interpretation
                      </h4>
                      <p className="text-xs text-base-content/70 mb-3 italic">
                        Heart–Lung Recovery Index
                      </p>
                      <div className="space-y-3">
                        <div>
                          <p className="font-semibold text-base-content">
                            &gt;107%{" "}
                            <span className="font-normal text-base-content/80">
                              Well above normal
                            </span>
                          </p>
                          <p className="text-xs text-base-content/70 mt-1">
                            Trend shows strong physiological recovery signals,
                            consistent with positive adaptation across heart,
                            lung, and autonomic systems.
                          </p>
                        </div>

                        <div>
                          <p className="font-semibold text-base-content">
                            103–107%{" "}
                            <span className="font-normal text-base-content/80">
                              Above normal
                            </span>
                          </p>
                          <p className="text-xs text-base-content/70 mt-1">
                            Trend reflects robust recovery patterns, with
                            markers reliably above baseline.
                          </p>
                        </div>

                        <div>
                          <p className="font-semibold text-base-content">
                            100–103%{" "}
                            <span className="font-normal text-base-content/80">
                              Normal optimal
                            </span>
                          </p>
                          <p className="text-xs text-base-content/70 mt-1">
                            Trend shows stable physiological recovery, with
                            heart and lung markers at or slightly above
                            baseline.
                          </p>
                        </div>

                        <div>
                          <p className="font-semibold text-base-content">
                            97–100%{" "}
                            <span className="font-normal text-base-content/80">
                              Normal stable
                            </span>
                          </p>
                          <p className="text-xs text-base-content/70 mt-1">
                            Trend reflects a steady and balanced recovery state
                            within expected day to day variation.
                          </p>
                        </div>

                        <div>
                          <p className="font-semibold text-base-content">
                            93–97%{" "}
                            <span className="font-normal text-base-content/80">
                              Below normal caution
                            </span>
                          </p>
                          <p className="text-xs text-base-content/70 mt-1">
                            Trend shows early physiological signs of reduced
                            recovery, with subtle downward shifts in heart and
                            lung markers.
                          </p>
                        </div>

                        <div>
                          <p className="font-semibold text-base-content">
                            &lt;93%{" "}
                            <span className="font-normal text-base-content/80">
                              Suppressed recovery
                            </span>
                          </p>
                          <p className="text-xs text-base-content/70 mt-1">
                            Trend shows clear signs of under recovery, with
                            markers meaningfully below baseline. This pattern
                            may also be associated with an increased risk of
                            illness.
                          </p>
                        </div>
                        <div>
                          <p>
                            Use your HLR score to assist with recovery and
                            training intensity decisions. Higher scores indicate
                            better recent cardiorespiratory recovery.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end">
                    <button
                      onClick={() => setShowInfoDialog(false)}
                      className="px-4 py-2 bg-neutral font-grotesk text-white rounded-md hover:bg-neutral/90 transition-colors font-medium"
                    >
                      Okay, got it
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Date Range Dialog for Quarter View */}
          {showDateRangeDialog && (
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
              <div className="bg-base-100 rounded-lg max-w-md w-full">
                <div className="p-6 pb-32">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-heading font-semibold text-base-content">
                      Custom Date Range
                    </h3>
                    <button
                      onClick={() => setShowDateRangeDialog(false)}
                      className="text-base-content/50 hover:text-base-content/70"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div className="new-form-group w-full">
                      <input
                        id="quarter-start-date"
                        type="date"
                        className="form-element cursor-not-allowed opacity-60"
                        value={(() => {
                          const startDate = new Date(targetDate);
                          startDate.setDate(startDate.getDate() - 83); // 12 weeks = 84 days, so 83 days back
                          return startDate.toISOString().split("T")[0];
                        })()}
                        disabled
                        style={{
                          colorScheme: "light",
                        }}
                      />
                      <label className="text-base-content/80">Start Date</label>
                    </div>

                    <div className="new-form-group w-full">
                      <input
                        id="quarter-end-date"
                        type="date"
                        className="form-element cursor-not-allowed opacity-60"
                        value={targetDate.toISOString().split("T")[0]}
                        disabled
                        style={{
                          colorScheme: "light",
                        }}
                      />
                      <label className="text-base-content/80">End Date</label>
                    </div>

                    {!isPremium && <ComingSoon featureKey="advancedGraphs" />}
                  </div>

                  <div className="mt-6 flex justify-end">
                    <button
                      onClick={() => setShowDateRangeDialog(false)}
                      className="px-4 py-2 bg-base-200 text-base-content/80 rounded-md hover:bg-base-300 transition-colors font-medium font-grotesk"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Export Dialog */}
          {showExportDialog && (
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
              <div className="bg-base-100 rounded-lg max-w-md w-full">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-heading font-semibold text-base-content">
                      Export to CSV
                    </h3>
                    <button
                      onClick={() => setShowExportDialog(false)}
                      className="text-base-content/50 hover:text-base-content/70"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>

                  {!isPremium && <ComingSoon featureKey="advancedGraphs" />}

                  <div className="mt-6 flex justify-end">
                    <button
                      onClick={() => setShowExportDialog(false)}
                      className="px-4 py-2 bg-base-200 text-base-content/80 rounded-md hover:bg-base-300 transition-colors font-medium font-grotesk"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Custom Date Range Modal */}
          {showCustomDateModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
              <div className="bg-base-100 rounded-lg max-w-md w-full">
                <div className="p-8">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-grotesk font-semibold text-base-content">
                      Custom Date Range
                    </h3>
                    <button
                      onClick={() => {
                        setShowCustomDateModal(false);
                        if (customRangeSleepData.length === 0) {
                          setSelectedTab("Day");
                        }
                      }}
                      className="p-1 rounded-full hover:bg-base-200 transition-colors"
                    >
                      <XMarkIcon className="h-5 w-5 text-base-content/60" />
                    </button>
                  </div>

                  {!hasCustomDateAccess && (
                    <ComingSoon featureKey="advancedGraphs" />
                  )}

                  <div className="space-y-4">
                    {/* Date pickers - always visible but disabled if no access */}
                    <div className="new-form-group w-full">
                      <input
                        id="custom-start-date"
                        type="date"
                        className={`form-element ${
                          hasCustomDateAccess
                            ? "cursor-pointer"
                            : "cursor-not-allowed opacity-60"
                        }`}
                        value={customStartDate}
                        onChange={(e) => setCustomStartDate(e.target.value)}
                        max={customEndDate || undefined}
                        disabled={!hasCustomDateAccess}
                        style={{
                          colorScheme: "light",
                          backgroundColor: "oklch(var(--b2))",
                          // iOS fix: Prevent date input from extending beyond container
                          maxWidth: "100%",
                          width: "100%",
                          fontSize: "16px", // Prevents iOS zoom on focus
                        }}
                        onFocus={(e) => {
                          if (hasCustomDateAccess) {
                            try {
                              e.target.showPicker?.();
                            } catch (error) {
                              // Fallback for browsers that don't support showPicker
                            }
                          }
                        }}
                      />
                      <label className="text-base-content/80">Start Date</label>
                    </div>

                    <div className="new-form-group w-full">
                      <input
                        id="custom-end-date"
                        type="date"
                        className={`form-element ${
                          hasCustomDateAccess
                            ? "cursor-pointer"
                            : "cursor-not-allowed opacity-60"
                        }`}
                        value={customEndDate}
                        onChange={(e) => setCustomEndDate(e.target.value)}
                        min={customStartDate || undefined}
                        disabled={!hasCustomDateAccess}
                        style={{
                          colorScheme: "light",
                          backgroundColor: "oklch(var(--b2))",
                          // iOS fix: Prevent date input from extending beyond container
                          maxWidth: "100%",
                          width: "100%",
                          fontSize: "16px", // Prevents iOS zoom on focus
                        }}
                        onFocus={(e) => {
                          if (hasCustomDateAccess) {
                            try {
                              e.target.showPicker?.();
                            } catch (error) {
                              // Fallback for browsers that don't support showPicker
                            }
                          }
                        }}
                      />
                      <label className="text-base-content/80">End Date</label>
                    </div>

                    {/* Load Data Button */}
                    {hasCustomDateAccess && (
                      <button
                        onClick={async () => {
                          if (customStartDate && customEndDate) {
                            await fetchCustomRangeData(
                              customStartDate,
                              customEndDate,
                            );
                            setSelectedTab("Custom");
                            setShowCustomDateModal(false);
                          }
                        }}
                        disabled={
                          !customStartDate ||
                          !customEndDate ||
                          customRangeLoading
                        }
                        className={`w-full py-3 px-4 rounded-xl font-grotesk font-medium transition-colors ${
                          !customStartDate ||
                          !customEndDate ||
                          customRangeLoading
                            ? "bg-base-300 text-base-content/60 cursor-not-allowed"
                            : "bg-primary text-white hover:bg-primary/90"
                        }`}
                      >
                        {customRangeLoading ? "Loading..." : "Load Data"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SleepInsights;
